if (!customElements.get("usp-banner")) {
  class USPBanner extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".usp-track");
      this.originalGroup = this.querySelector(".usp-group");
      this.autoScrollSetting = this.dataset.autoScroll;

      this.speed = parseFloat(this.dataset.speed) || 15;
      this.step = (this.speed / 50) * 1.5 + 0.2;
      this.currentScrollPos = 0;
      this.animationFrame = null;
      this.isHoveredOrTouched = false;
      this.groupWidth = 0;
      this.resumeTimer = null;
      this.currentTranslateX = 0;
      this.baseCloneCount = 3;
      this.maxDynamicClones = 8;
      this.deferredInitTimer = null;
      this.intersectionObserver = null;
      this.idleCoverageHandle = null;
      this.idleCoverageTimer = null;

      this.init = this.init.bind(this);
      this.scrollLoop = this.scrollLoop.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

      this.pauseScroll = () => {
        clearTimeout(this.resumeTimer);
        this.isHoveredOrTouched = true;
      };

      this.resumeScroll = () => {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => {
          this.isHoveredOrTouched = false;
        }, 300);
      };

      this.boundResize = this.debounce(this.init, 250);
      this.setupDeferredStart();

      window.addEventListener("resize", this.boundResize);
      this.track.addEventListener("mouseenter", this.pauseScroll);
      this.track.addEventListener("mouseleave", this.resumeScroll);
      this.track.addEventListener("touchstart", this.pauseScroll, { passive: true });
      this.track.addEventListener("touchend", this.resumeScroll);
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    disconnectedCallback() {
      this.stopScroll();
      clearTimeout(this.deferredInitTimer);
      clearTimeout(this.resumeTimer);
      clearTimeout(this.idleCoverageTimer);
      this.deferredInitTimer = null;
      this.resumeTimer = null;
      this.cancelIdleCoverage();

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      window.removeEventListener("resize", this.boundResize);
      this.track.removeEventListener("mouseenter", this.pauseScroll);
      this.track.removeEventListener("mouseleave", this.resumeScroll);
      this.track.removeEventListener("touchstart", this.pauseScroll);
      this.track.removeEventListener("touchend", this.resumeScroll);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }

    setupDeferredStart() {
      const start = () => {
        requestAnimationFrame(() => {
          this.deferredInitTimer = setTimeout(() => {
            this.init();
            this.deferredInitTimer = null;
          }, 120);
        });
      };

      if (!("IntersectionObserver" in window)) {
        start();
        return;
      }

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (!entries[0] || !entries[0].isIntersecting) return;
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
        start();
      });

      this.intersectionObserver.observe(this);
    }

    init() {
      const isDesktop = window.innerWidth >= 768;
      let shouldScroll = false;

      if (this.autoScrollSetting === "both") shouldScroll = true;
      if (this.autoScrollSetting === "desktop" && isDesktop) shouldScroll = true;
      if (this.autoScrollSetting === "mobile" && !isDesktop) shouldScroll = true;

      this.stopScroll();
      this.ensureMinimumClones(this.baseCloneCount);
      this.groupWidth = this.originalGroup.getBoundingClientRect().width;
      this.scheduleCoverageInIdle();

      if (shouldScroll) {
        this.classList.add("is-scrolling-active");
        this.currentScrollPos = 0;
        this.currentTranslateX = 0;
        this.track.style.transform = "translate3d(0, 0, 0)";
        this.scrollLoop();
      } else {
        this.classList.remove("is-scrolling-active");
        this.currentScrollPos = 0;
        this.currentTranslateX = 0;
        this.track.style.transform = "";
      }
    }

    ensureMinimumClones(minClones) {
      const existingClones = this.track.querySelectorAll(".usp-group-clone").length;
      const required = Math.max(0, minClones - existingClones);
      for (let i = 0; i < required; i += 1) {
        const clone = this.originalGroup.cloneNode(true);
        clone.classList.add("usp-group-clone");
        clone.setAttribute("aria-hidden", "true");
        this.track.appendChild(clone);
      }
    }

    ensureTrackCoverage() {
      const hostWidth = this.getBoundingClientRect().width;
      if (!hostWidth) return;

      this.ensureMinimumClones(this.baseCloneCount);
      if (this.track.scrollWidth >= hostWidth * 2) {
        this.groupWidth = this.originalGroup.getBoundingClientRect().width;
        return;
      }

      let safetyCounter = 0;

      while (this.track.scrollWidth < hostWidth * 2 && safetyCounter < this.maxDynamicClones) {
        const clone = this.originalGroup.cloneNode(true);
        clone.classList.add("usp-group-clone");
        clone.setAttribute("aria-hidden", "true");
        this.track.appendChild(clone);
        safetyCounter += 1;
      }
      this.groupWidth = this.originalGroup.getBoundingClientRect().width;
    }

    scheduleCoverageInIdle() {
      this.cancelIdleCoverage();

      const runCoverage = () => {
        this.idleCoverageHandle = null;
        this.idleCoverageTimer = null;
        this.ensureTrackCoverage();
      };

      if ("requestIdleCallback" in window) {
        this.idleCoverageHandle = window.requestIdleCallback(runCoverage, { timeout: 400 });
        return;
      }

      this.idleCoverageTimer = setTimeout(runCoverage, 220);
    }

    cancelIdleCoverage() {
      if (this.idleCoverageHandle && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(this.idleCoverageHandle);
      }
      this.idleCoverageHandle = null;
      if (this.idleCoverageTimer) clearTimeout(this.idleCoverageTimer);
      this.idleCoverageTimer = null;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopScroll();
        return;
      }
      if (this.classList.contains("is-scrolling-active") && !this.animationFrame) {
        this.scrollLoop();
      }
    }

    scrollLoop() {
      if (!this.isHoveredOrTouched && this.classList.contains("is-scrolling-active")) {
        if (this.groupWidth > 0) {
          this.currentScrollPos += this.step;
          if (this.currentScrollPos >= this.groupWidth) {
            this.currentScrollPos -= this.groupWidth;
          }
          this.currentTranslateX = this.currentScrollPos * -1;
          this.track.style.transform = `translate3d(${this.currentTranslateX}px, 0, 0)`;
        }
      }
      this.animationFrame = requestAnimationFrame(this.scrollLoop);
    }

    stopScroll() {
      if (!this.animationFrame) return;
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }
  }

  customElements.define("usp-banner", USPBanner);
}
